import { Dispatch, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApplicationStatus, DocStatus, DocTypes } from 'components/DocBox/DocBox.types';
import { STATUS } from 'constants/constants';
import { ApplicationDetailsActionTypes } from 'datasource/useApplicationDetails/ApplicationDetailsManager.types';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import { getClientId } from 'utils';

/**
 * Handles application-document generation for both the 3aVB (IBAN reservation + generate) and
 * the 3b (generateDocumentsNew) flows, plus the retry state. Extracted verbatim from
 * ApplicationDetailsManager (dep arrays preserved).
 */
export const useDocumentGeneration = ({
    dispatch,
    refetchNon3bApplicationsDocuments,
    dfsClientId,
}: {
    dispatch: Dispatch<any>;
    refetchNon3bApplicationsDocuments: () => Promise<any>;
    dfsClientId: string;
}) => {
    const { i18n: { language } } = useTranslation();
    const { mutateAsync: handleMutation, isPending } = useGenericAPIMutation();

    const contactId = getClientId(dfsClientId);

    const onDocGeneration = useCallback(async ({ applicationId, applicationStatus, is3aVB }) => {
        dispatch({
            type: ApplicationDetailsActionTypes.UPDATE_GENERATE_APPLICATION_DOC,
            payload: {
                applicationId,
                status: DocStatus.Loading,
            },
        });

        let data;

        if (is3aVB) {
            if (applicationStatus === ApplicationStatus.readyForIbanReservation) {
                await handleMutation({
                    service: 'customInvestmentService',
                    API: 'makeIbanReservation',
                    payload: {
                        investmentApplicationId: applicationId,
                    },
                });

                await handleMutation({
                    service: 'customInvestmentService',
                    API: 'generateDocuments',
                    payload: {
                        investmentApplicationId: applicationId,
                    },
                });

                const response = await refetchNon3bApplicationsDocuments();

                // refetch() returns the useGetCustomService query result; its `data` is the FULL
                // service response, so the documents array is at `response.data.data`.
                data = { documents: response?.data?.data || [] };
            } else if ([ApplicationStatus.readyForSign, ApplicationStatus.documentGenerationFailed]?.includes(applicationStatus)) {
                await handleMutation({
                    service: 'customInvestmentService',
                    API: 'generateDocuments',
                    payload: {
                        investmentApplicationId: applicationId,
                    },
                });

                const response = await refetchNon3bApplicationsDocuments();

                // See note above: documents array is at `response.data.data`.
                data = { documents: response?.data?.data || [] };
            }
        } else {
            const response = await handleMutation({
                service: 'customInvestmentService',
                API: 'generateDocumentsNew',
                payload: {
                    investmentApplicationId: applicationId, language,
                },
            });

            data = (response as any)?.data;
        }

        if (data) {
            dispatch({
                type: ApplicationDetailsActionTypes.ADD_GENERATED_DOCS_FOR_APPLICATION,
                payload: {
                    applicationId,
                    documents: data?.documents?.map((doc) => ({
                        applicationId,
                        documentId: doc?.id,
                        documentName: doc?.metadataName || doc?.documentName,
                        status: doc?.signStatus === STATUS.submitted ? DocStatus.ReadyToSubmit : DocStatus.ReadyToSign,
                        type: DocTypes.APPLICATION,
                    })),
                },
            });
        }
    }, [refetchNon3bApplicationsDocuments, language, handleMutation]);

    // Hook for handling document generation with the new API
    const onDocGenerationWithNewAPI = useCallback(async ({ applicationId, applicationStatus, is3aVB }) => {
        dispatch({
            type: ApplicationDetailsActionTypes.UPDATE_GENERATE_APPLICATION_DOC,
            payload: {
                applicationId,
                status: DocStatus.Loading,
            },
        });

        let data;

        if (is3aVB) {
            if (applicationStatus === ApplicationStatus.readyForIbanReservation) {
                await handleMutation({
                    service: 'customInvestmentService',
                    API: 'makeIbanReservation',
                    payload: {
                        investmentApplicationId: applicationId,
                    },
                });

                await handleMutation({
                    service: 'customInvestmentService',
                    API: 'generateDocumentsWithNewAPI',
                    payload: {
                        contactId,
                        investmentApplicationId: applicationId,
                        language,
                    },
                });

                const response = await refetchNon3bApplicationsDocuments();

                // refetch() returns the useGetCustomService query result; its `data` is the FULL
                // service response, so the documents array is at `response.data.data`.
                data = { documents: response?.data?.data || [] };
            } else if ([ApplicationStatus.readyForSign, ApplicationStatus.documentGenerationFailed]?.includes(applicationStatus)) {
                await handleMutation({
                    service: 'customInvestmentService',
                    API: 'generateDocumentsWithNewAPI',
                    payload: {
                        contactId,
                        investmentApplicationId: applicationId,
                        language,
                    },
                });

                const response = await refetchNon3bApplicationsDocuments();

                // See note above: documents array is at `response.data.data`.
                data = { documents: response?.data?.data || [] };
            }
        } else {
            const response = await handleMutation({
                service: 'customInvestmentService',
                API: 'generateDocumentsWithNewAPI',
                payload: {
                    contactId,
                    investmentApplicationId: applicationId,
                    language,
                },
            });

            data = (response as any)?.data;
        }

        if (data) {
            dispatch({
                type: ApplicationDetailsActionTypes.ADD_GENERATED_DOCS_FOR_APPLICATION,
                payload: {
                    applicationId,
                    documents: data?.documents?.map((doc) => ({
                        applicationId,
                        documentId: doc?.id,
                        documentName: doc?.metadataName || doc?.documentName,
                        status: doc?.signStatus === STATUS.submitted ? DocStatus.ReadyToSubmit : DocStatus.ReadyToSign,
                        type: DocTypes.APPLICATION,
                    })),
                },
            });
        }
    }, [refetchNon3bApplicationsDocuments, language, handleMutation, contactId]);

    const setRetryState = useCallback(({ applicationId }) => {
        dispatch({
            type: ApplicationDetailsActionTypes.UPDATE_GENERATE_APPLICATION_DOC,
            payload: {
                applicationId,
                status: DocStatus.Error,
            },
        });
    }, []);

    return {
        onDocGeneration, onDocGenerationWithNewAPI, setRetryState, is3BDocGenerationInProgress: isPending,
    };
};
